import React from 'react';
import PropTypes from 'prop-types';
import {
  Spacer,
  BreakPointItem,
  BREAKPOINTS,
  AlertBase as Alert,
  Visible,
} from '@tfecom-thermofisher/react-komodo-design';

import { STEPS } from 'src/constants/forms';
import generalMessages from 'src/messages';
import { Message } from 'src/core/components';
import { FooterBar } from 'src/components/OligoDNA/components';
import UploadPlateTemplate from 'src/components/OligoDNA/components/UploadPlateTemplate';
import PlateSizeDetails from 'src/components/OligoDNA/components/PlateSizeAndDetails';
import NormalizationAliquots from 'src/components/OligoDNA/components/NormalizationAliquots';
import PlatePooling from 'src/components/OligoDNA/components/PlatePooling';
import ReviewAndAddToCart from 'src/components/OligoDNA/components/ReviewAndAddToCart';

import { TabNode } from './components';
import './ProgressTabs.style.scss';

const componentBySteps = {
  [STEPS.UPLOAD_FILE]: UploadPlateTemplate,
  [STEPS.PLATE_SIZE_DETAILS]: PlateSizeDetails,
  [STEPS.NORMALIZATION_ALIQUOTS]: NormalizationAliquots,
  [STEPS.PLATE_POOLING]: PlatePooling,
  [STEPS.REVIEW_AND_SUBMIT]: ReviewAndAddToCart
};

const ProgressTabs = ({
  dnaSteps,
  onClickTab,
  onNext,
  onPrevious,
  showFooterPrevious,
  showOrderSummaryMobile,
  footerNextMessage,
  selectedTab,
  actionErrors,
  clearActionError,
  canContinue,
  onFileDetailsEdit,
  onPlateSizeDetailsEdit,
  onNormalizationAliquotsDetailsEdit,
  onPlatePoolingDetailsEdit,
  getContinueBtnTooltipMessage,
  selectedStep,
}) => {
  const Component = componentBySteps[selectedStep?.name];
  const content = Component ? (
    <Component
      {...{
        onFileDetailsEdit,
        onPlateSizeDetailsEdit,
        onNormalizationAliquotsDetailsEdit,
        onPlatePoolingDetailsEdit,
      }}
    />
  ) : null;

  return (
    <>
      <div className="dna-progress-tabs">
        {dnaSteps.map((tab) => (
          <TabNode
            key={`tab-${tab.order}`}
            tab={tab}
            onClick={onClickTab}
            selectedTab={selectedTab}
            onFileDetailsEdit={onFileDetailsEdit}
            onPlateSizeDetailsEdit={onPlateSizeDetailsEdit}
            onNormalizationAliquotsDetailsEdit={
              onNormalizationAliquotsDetailsEdit
            }
            onPlatePoolingDetailsEdit={onPlatePoolingDetailsEdit}
            content={content}
            canContinue={canContinue}
          />
        ))}
      </div>

      <BreakPointItem min={BREAKPOINTS.md}>
        <Spacer bottom={40} />
        {content}
      </BreakPointItem>

      <Visible when={!!actionErrors?.length}>
        <Spacer top={25} />
        {actionErrors?.map((errorMessage) => (
          <>
            <Alert
              data-testid="dna-oligos-action-error"
              float
              white
              type="error"
              onClose={() => clearActionError(errorMessage)}
            >
              <Message message={errorMessage} />
            </Alert>
            <Spacer top={25} />
          </>
        ))}
      </Visible>

      <FooterBar
        footerNext={footerNextMessage}
        footerPrevious={generalMessages.footerPrevious}
        onNext={onNext}
        onPrevious={onPrevious}
        showFooterPrevious={showFooterPrevious}
        showOrderSummaryMobile={showOrderSummaryMobile}
        canContinue={canContinue}
        getContinueBtnTooltipMessage={getContinueBtnTooltipMessage}
      />
    </>
  );
};

ProgressTabs.propTypes = {
  dnaSteps: PropTypes.array.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onClickTab: PropTypes.func.isRequired,
  showFooterPrevious: PropTypes.bool.isRequired,
  showOrderSummaryMobile: PropTypes.bool.isRequired,
  footerNextMessage: PropTypes.object.isRequired,
  selectedTab: PropTypes.object,
  actionErrors: PropTypes.array,
  clearActionError: PropTypes.func.isRequired,
  canContinue: PropTypes.bool.isRequired,
  onFileDetailsEdit: PropTypes.func.isRequired,
  onPlateSizeDetailsEdit: PropTypes.func.isRequired,
  onNormalizationAliquotsDetailsEdit: PropTypes.func.isRequired,
  onPlatePoolingDetailsEdit: PropTypes.func.isRequired,
  getContinueBtnTooltipMessage: PropTypes.func.isRequired,
  selectedStep: PropTypes.object.isRequired,
};

export default ProgressTabs;
